// @flow
import { getPartnerActivityType } from "../../app/assets/pois/utils/createPartnerActivity";
import stripAccents from "../lib/stripAccents";
import cleanString from "../lib/cleanString";
import constants from "../lib/constants";
import {
  USER_FRIENDLY_MAP,
  RESOURCE_ABBREV_MAP,
  RESOURCE_KIND_ABBREV_MAP,
} from "../../server/routes/constants";
import resourceTypes from "../lib/open-planet/resourceTypes";
import type { KindEnum, ResourceEnum, IResource, Poi } from "../types";

/**
 * Pass in an array of objects with a title or name and get back a slug
 * @example
 *
 * createSlug([{ title: "usa" }, { title: "nashville" }, { name: "foo tour" }])
 * // usa/nashville/sights/foo-tour
 */
export function createSlug(breadcrumbs: string[], ...additionalResources): string {
  const filteredBreadcrumbs = breadcrumbs.filter((n) => n);
  const reducedBreadcrumbs = filteredBreadcrumbs.length === 1 ? filteredBreadcrumbs : filteredBreadcrumbs.slice(1,3);
  const slugParts = [
    ...reducedBreadcrumbs,
    ...additionalResources,
  ];
  return `/${slugParts.map(name => cleanString(name)).join("/")}`;
}

function getPartnerActivitySlug(resource: Object) {
  return getPartnerActivityType(resource.duration);
}

function listUrl({
  slug, placeId, devPart,
}: {
  placeId: number,
  slug: string,
  devPart: string,
}) {
  const slash = slug[0] === "/" ? "" : "/";

  return `${slash}${stripAccents(slug)}/` +
    `${constants.AJAX_PATH}/${devPart}/${placeId}`;
}

// usa/nashville/activities/a/pa-act/362228
// usa/nashville/attractions/a/poi-sig/362228
export function createListUrl({
  breadcrumb,
  kind = "",
  placeId,
  resource,
}: {
  breadcrumb: Array<string>,
  kind: KindEnum,
  placeId: number,
  resource: ResourceEnum,
}) {
  // const isPartnerActivity = resource.type === resourceTypes.partner_activity;
  const userFriendlyResource = USER_FRIENDLY_MAP[kind] || USER_FRIENDLY_MAP[resource];

  const slug = createSlug(breadcrumb, userFriendlyResource);
  const kindAbbrev = RESOURCE_KIND_ABBREV_MAP[kind] || "";
  const resourceAbbrev = RESOURCE_ABBREV_MAP[resource];
  const devPart = `${resourceAbbrev}${kindAbbrev ? `-${kindAbbrev}` : ""}`;

  return listUrl({
    slug,
    placeId,
    devPart,
  });
}

export function createPoiUrl({
  slug,
  resource,
  id,
  placeId,
  kind = "",
}: {
  id: number,
  slug: string,
  resource: string,
  kind: string,
  placeId: number,
}): string {
  const sKind = kind ? `-${kind}` : "";
  const sPlace = placeId ? `/${placeId}` : "";

  return `${slug}/${constants.AJAX_PATH}/${resource}${sKind}/${id}${sPlace}`;
}

/**
 * Generate a POI url from a given resource, breadcrumbs, and containing place id
 */
export function createDetailUrlFromResource(
  resource: IResource,
  breadcrumb: Array<string>,
  placeId: number,
): string {
  const isPartnerActivity = resource.type === resourceTypes.partner_activity;
  const isCollection = resource.type === resourceTypes.narrative;
  let resourceName = resource.title || resource.name;

  let kind = resource.type;
  if (resource.type === resourceTypes.poi) {
    const poi: Poi = resource;
    kind = poi.poiType;
  }

  /*
    Needs a constants.js refactor to accept resource.type without modifications.
  */
  if (isCollection) {
    resource.type = "collections";
    resourceName = (resource.content[0]) ? resource.content[0].title : null;
    kind = resourceTypes.collections;
  }

  let userFriendlyResource = isPartnerActivity ?
    getPartnerActivitySlug(resource) :
    USER_FRIENDLY_MAP[kind];

  if (isCollection && resourceName === "Sleeping") {
    resourceName = `Best places to stay in ${resource.places.name}`;
    userFriendlyResource = "hotels";
  }

  const slug = createSlug(breadcrumb, userFriendlyResource, resourceName);

  let kindShort;
  if (isPartnerActivity) {
    kindShort = RESOURCE_KIND_ABBREV_MAP[userFriendlyResource];
  } else if (isCollection) {
    kindShort = "";
  } else {
    kindShort = RESOURCE_KIND_ABBREV_MAP[kind];
  }

  return createPoiUrl({
    id: resource.id,
    slug,
    resource: RESOURCE_ABBREV_MAP[resource.type],
    kind: kindShort,
    placeId,
  });
}



// WEBPACK FOOTER //
// ../server/utils/createUrls.js